import React, { useState, useMemo } from "react"
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Stack,
  Pagination,
  Button,
} from "@mui/material"
import * as Style from "./style"
import { Link, useNavigate } from "react-router-dom"
import { useAcceptGenrateBookingQuery, useGetAcceptedGeneratedPostsAllQuery } from "../../features/OverViewAuth/overViewApi"
import {
  FormatChangeTime,
  FormatDate,
  FormatTime,
} from "../common/utctolocalDate"
import dummyImage from "../../images/dummy-icon.png"
import noDataUpcoming from "../../images/no-results.svg"
import CenteredSpinner from "../common/CenteredSpinner"

const CELL = { fontWeight: 700, color: "#555", fontSize: "0.85rem" }

const PAY_NOW_BTN = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: "#F475A4",
  fontSize: "0.75rem",
  width: 80,
  minWidth: 80,
  maxWidth: 80,
  height: 28,
  lineHeight: "28px",
  p: 0,
  overflow: "hidden",
  "&:hover": { backgroundColor: "#D45E8C" },
}

const MyBookings = () => {
  const [selectedPage, setSelectedPage] = useState(1)
  const navigate = useNavigate()

  const {
    data: upcomingBooking,
    isFetching,
    isLoading,
  } = useAcceptGenrateBookingQuery({ pageNo: selectedPage })

  const { data: allBookingsData } = useGetAcceptedGeneratedPostsAllQuery({})

  const unpaidBookings = useMemo(() => {
    if (!allBookingsData?.data || !Array.isArray(allBookingsData.data)) return [];
    return allBookingsData.data.filter((item: any) =>
      !item.ispaymentVeified &&
      (item.isDraft || item.isConfirm || item.status === "Generated")
    );
  }, [allBookingsData]);

  const handlePageChange = (_: any, value: any) => setSelectedPage(value)

  const renderDate = (item: any) => {
    if (!item.from) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
      <Box sx={{ lineHeight: 1.5, textAlign: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
          <FormatDate dateString={item.from} />
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          <FormatTime dateString={item.from} /> <FormatChangeTime dateString={item.from} />
        </Typography>
      </Box>
    );
  }

  const renderEndDate = (item: any) => {
    if (!item.till) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
      <Box sx={{ lineHeight: 1.5, textAlign: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
          <FormatDate dateString={item.till} />
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          <FormatTime dateString={item.till} /> <FormatChangeTime dateString={item.till} />
        </Typography>
      </Box>
    );
  }

  const renderProvider = (item: any) => {
    if (item?.assignedTo?.length > 0) {
      const p = item.assignedTo[0];
      return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <Box component="img" src={p?.profilePic || dummyImage} sx={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
          <Box textAlign="left">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p?.name || "—"}</Typography>
            {item.assignedTo.length > 1 && <Typography variant="caption" color="text.secondary">+{item.assignedTo.length - 1}</Typography>}
          </Box>
        </Box>
      );
    }
    return <Typography variant="caption" color="error">No Provider Assigned</Typography>;
  }

  const renderAction = (item: any) => {
    if (item.status === "Accepted" || item.status === "Active" || item.status === "Inprogress") {
      return (
        <Link to={`/dashboard/bookings/details/${item?._id}`} style={{ color: "#F475A4", fontWeight: 700, fontSize: "0.82rem", textDecoration: "none", whiteSpace: "nowrap" }}>
          Track Service
        </Link>
      );
    }
    if (!item.ispaymentVeified && (item.isDraft || item.isConfirm || item.status === "Generated")) {
      return (
        <Button
          variant="contained"
          disableRipple
          disableFocusRipple
          disableTouchRipple
          sx={PAY_NOW_BTN}
          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/pending-payments/${item._id}`); }}
        >
          Pay Now
        </Button>
      );
    }
    return <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.82rem" }}>—</Typography>;
  }

  const renderStatus = (item: any) => {
    if (item.ispaymentVeified) {
      return <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: 600, fontSize: "0.82rem" }}>Paid</Typography>;
    }
    if (item.status === "Generated") {
      return <Typography variant="body2" sx={{ color: "#ed6c02", fontWeight: 600, fontSize: "0.82rem" }}>Pending Payment</Typography>;
    }
    if (item.status === "Accepted") {
      return <Typography variant="body2" sx={{ color: "#1565c0", fontWeight: 600, fontSize: "0.82rem" }}>Confirmed</Typography>;
    }
    if (item.status === "Active") {
      return <Typography variant="body2" sx={{ color: "#1565c0", fontWeight: 600, fontSize: "0.82rem" }}>In Progress</Typography>;
    }
    if (item.status === "Completed") {
      return <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: 600, fontSize: "0.82rem" }}>Completed</Typography>;
    }
    if (item.status === "Expired") {
      return <Typography variant="body2" sx={{ color: "#999", fontWeight: 600, fontSize: "0.82rem" }}>Expired</Typography>;
    }
    return <Typography variant="body2" color="text.secondary">{item.status || "—"}</Typography>;
  }

  // Combine unpaid + upcoming into one list. Upcoming excludes unpaid to avoid dupes.
  const combinedList = useMemo(() => {
    const unpaidIds = new Set((unpaidBookings || []).map((b: any) => b._id));
    const upcoming = (upcomingBooking?.data || []).filter((b: any) => !unpaidIds.has(b._id));
    return [...(unpaidBookings || []), ...upcoming];
  }, [unpaidBookings, upcomingBooking?.data]);

  return (
    <>
      {isFetching || isLoading ? (
        <CenteredSpinner />
      ) : (
        <Box sx={{ px: { xs: 1, md: 3 } }}>
          <Style.Overview>
            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#333", mb: 2, mt: 4 }}>
              My Bookings
            </Typography>

            <TableContainer component={Paper} sx={{ borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} className="dashboard-table-wrapper custom-scrollbar">
              <Table sx={{ minWidth: 650, border: "none" }} className="dashboard-table">
                {combinedList.length > 0 && (
                  <TableHead>
                    <TableRow>
                      <TableCell sx={CELL}></TableCell>
                      <TableCell align="center" sx={CELL}>Provider</TableCell>
                      <TableCell align="center" sx={CELL}>Service</TableCell>
                      <TableCell align="center" sx={CELL}>Start Time</TableCell>
                      <TableCell align="center" sx={CELL}>End Time</TableCell>
                      <TableCell align="center" sx={CELL}>Status</TableCell>
                      <TableCell align="center" sx={CELL}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {combinedList.length > 0 ? (
                    combinedList.map((item: any) => (
                      <TableRow
                        key={item._id}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer", "&:hover": { bgcolor: "#fafafa" } }}
                        onClick={() => navigate(`/dashboard/bookings/details/${item?._id}`)}
                      >
                        <TableCell align="center">
                          <Box component="img" src={item?.assignedTo?.[0]?.profilePic || dummyImage} sx={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", opacity: item?.assignedTo?.length ? 1 : 0.4 }} />
                        </TableCell>
                        <TableCell align="center">{renderProvider(item)}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item?.service?.name || "—"}</Typography>
                        </TableCell>
                        <TableCell align="center">{renderDate(item)}</TableCell>
                        <TableCell align="center">{renderEndDate(item)}</TableCell>
                        <TableCell align="center">{renderStatus(item)}</TableCell>
                        <TableCell align="center">{renderAction(item)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ border: "none", py: 6 }}>
                        <img src={noDataUpcoming} style={{ width: "100%", maxWidth: "160px", opacity: 0.5 }} alt="no bookings" />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No bookings found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {upcomingBooking?.data?.length > 0 && (
              <Box className="d-md-flex justify-content-between align-items-center mt-3">
                <Typography variant="body2" color="text.secondary">
                  Showing {upcomingBooking?.totalDocument?.showingData} / {upcomingBooking?.totalDocument?.totalCount} Total
                </Typography>
                <Stack spacing={2} className="mb-3" sx={{ mt: { xs: 1, md: 0 } }}>
                  <Pagination
                    count={upcomingBooking?.totalDocument?.totalPages}
                    color="secondary"
                    className="custom-pagination"
                    page={selectedPage}
                    onChange={handlePageChange}
                    sx={{ "& .MuiPaginationItem-page.Mui-selected": { backgroundColor: "#F475A4" } }}
                  />
                </Stack>
              </Box>
            )}
          </Style.Overview>
        </Box>
      )}
    </>
  )
}

export default MyBookings
